import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { SnackbarService } from '../../snackbar.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { B2bMeContext, B2bMembership, LoginModel, Tokens } from '@app/core/models';
import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';

export const B2B_ALLOWED_ROLES = [
  'ROLE_B2B_ADMIN',
  'ROLE_B2B_OPERATOR',
  'ROLE_B2B_MANAGER_LEVEL_1',
  'ROLE_B2B_MANAGER_LEVEL_2',
] as const;

/** Агент партнера (учетная запись создается через POST /api/b2b/admin/partners/{agentId}/account) */
export const AGENT_ROLES = ['ROLE_PARTNER_AGENT'] as const;

export const FULL_ACCESS_ROLES = [
  'ROLE_SUPER_ADMIN',
  'ROLE_ADMIN',
  'ROLE_B2B_ADMIN',
] as const;

export const OPERATOR_ACCESS_ROLES = [...FULL_ACCESS_ROLES, 'ROLE_B2B_OPERATOR'] as const;

export const EXCHANGE_ACCESS_ROLES = [
  ...OPERATOR_ACCESS_ROLES,
  'ROLE_B2B_MANAGER_LEVEL_1',
  'ROLE_B2B_MANAGER_LEVEL_2',
  // агент оформляет обмены в магазине работодателя
  ...AGENT_ROLES,
] as const;

export const SALES_AND_REFUNDS_ACCESS_ROLES = [
  ...FULL_ACCESS_ROLES,
  'ROLE_B2B_OPERATOR',
  'ROLE_B2B_MANAGER_LEVEL_1',
] as const;

/** Продажи (реестр и детали): как продажи/возвраты + агент — без доступа к заявкам на возврат */
export const SALES_ACCESS_ROLES = [...SALES_AND_REFUNDS_ACCESS_ROLES, ...AGENT_ROLES] as const;

export const ADMIN_ALLOWED_ROLES = [
  ...OPERATOR_ACCESS_ROLES,
  'ROLE_B2B_MANAGER_LEVEL_1',
  'ROLE_B2B_MANAGER_LEVEL_2',
  ...AGENT_ROLES,
] as const;

export const B2B_SCOPED_ROLES = [
  'ROLE_B2B_OPERATOR',
  'ROLE_B2B_MANAGER_LEVEL_1',
  'ROLE_B2B_MANAGER_LEVEL_2',
  // агент работает в магазине работодателя — продажа оформляется от его имени
  ...AGENT_ROLES,
] as const;

export interface DecodedToken {
  sub?: string;
  userId?: string;
  email?: string;
  username?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  authorities?: string[];
  permissions?: string[];
  accessType?: string;
  agentCooperationMode?: string;
  partnerId?: string;
  partnerNetworkId?: string;
  partnerStoreId?: string;
  exp?: number;
  iat?: number;

  [key: string]: unknown;
}

export interface UserInfo {
  id: string;
  email: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  permissions: string[];
  tokenExpiry: Date | null;
}

export interface B2bAccessScope {
  restricted: boolean;
  roles: string[];
  accessType: string;
  partnerId: string;
  partnerNetworkId: string;
  partnerStoreId: string;
  /** Привязка из GET /b2b/me/context (названия партнера, сети, точки, агент); null — контекст не загружен */
  membership: B2bMembership | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService {
  private API_BASE: string = inject(API_BASE_URL);
  private http: HttpClient = inject(HttpClient);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private translate: TranslateService = inject(TranslateService);
  private router: Router = inject(Router);

  private readonly refreshInterval = 20 * 60 * 1000;

  /**
   * Контекст текущего пользователя (GET /b2b/me/context): партнер, сеть и точка.
   * Грузится один раз перед входом в админку (b2bContextGuard); у полного админа не запрашивается.
   */
  private readonly b2bContext = signal<B2bMeContext | null>(null);
  /** Для какого пользователя загружен контекст — при входе под другим грузим заново */
  private b2bContextOwner: string | null = null;
  private b2bContextRequest$: Observable<B2bMeContext | null> | null = null;

  decodeToken(token: string): DecodedToken | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid JWT format');
        return null;
      }

      const payload = parts[1];
      const decoded = this.base64UrlDecode(payload);
      const parsed = JSON.parse(decoded) as DecodedToken;
      console.log('[Auth] Decoded access token payload:', parsed);
      return parsed;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  private base64UrlDecode(str: string): string {
    str = str.replace(/-/g, '+').replace(/_/g, '/');

    switch (str.length % 4) {
      case 0:
        break;
      case 2:
        str += '==';
        break;
      case 3:
        str += '=';
        break;
      default:
        throw new Error('Invalid base64url string');
    }

    return atob(str);
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  getDecodedAccessToken(): DecodedToken | null {
    const token = this.getAccessToken();
    return token ? this.decodeToken(token) : null;
  }

  isTokenExpired(token?: string): boolean {
    const tokenToCheck = token || this.getAccessToken();
    if (!tokenToCheck) return true;

    const decoded = this.decodeToken(tokenToCheck);
    if (!decoded || !decoded.exp) return true;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp < now;
  }

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return token != null && !this.isTokenExpired(token);
  }

  isB2bAuthorized(): boolean {
    return this.isAuthenticated() && this.hasAnyRole([...ADMIN_ALLOWED_ROLES]);
  }

  getCurrentUserInfo(): UserInfo | null {
    const decoded = this.getDecodedAccessToken();
    if (!decoded) return null;

    return {
      id: decoded.sub || decoded.userId || '',
      email: decoded.email || '',
      username: decoded.username || decoded.name || '',
      firstName: decoded.firstName || '',
      lastName: decoded.lastName || '',
      roles: Array.isArray(decoded.roles)
        ? decoded.roles
        : Array.isArray(decoded.authorities)
        ? decoded.authorities
        : [],
      permissions: Array.isArray(decoded.permissions) ? decoded.permissions : [],
      tokenExpiry: decoded.exp ? new Date(decoded.exp * 1000) : null,
    };
  }

  hasRole(role: string): boolean {
    const userInfo = this.getCurrentUserInfo();
    return userInfo?.roles?.includes(role) || false;
  }

  hasAnyRole(roles: string[]): boolean {
    const userInfo = this.getCurrentUserInfo();
    if (!userInfo?.roles || !Array.isArray(userInfo.roles)) return false;

    return roles.some(role => userInfo.roles.includes(role));
  }

  /**
   * Стартовая страница после входа: агент с рекламой — свой кабинет («Мой маркетинг»),
   * агент без рекламы («Продажи и обмен») — продажи, остальные — обмены
   */
  getHomeRoute(): string[] {
    if (!this.isOnlyAgent()) return ['admin', 'management', 'exchange'];

    return this.getAgentCooperationMode() === 'SALES_AND_EXCHANGE'
      ? ['admin', 'management', 'sales']
      : ['admin', 'agent', 'marketing'];
  }

  /** Агент «Только реклама»: доступен только свой кабинет («Мой маркетинг») — без продаж и обменов */
  isAdvertisingOnlyAgent(): boolean {
    return this.isOnlyAgent() && this.getAgentCooperationMode() === 'ADVERTISING_ONLY';
  }

  /** Пользователь — только агент, без других ролей админки */
  private isOnlyAgent(): boolean {
    return (
      this.hasAnyRole([...AGENT_ROLES]) &&
      !this.hasAnyRole(ADMIN_ALLOWED_ROLES.filter(r => !(AGENT_ROLES as readonly string[]).includes(r)))
    );
  }

  /** Режим сотрудничества агента: из /b2b/me/context, пока он не загружен — из токена */
  private getAgentCooperationMode(): string | null {
    const membership = this.getB2bAccessScope().membership;
    const decoded = this.getDecodedAccessToken();
    const tokenMode = typeof decoded?.agentCooperationMode === 'string' ? decoded.agentCooperationMode : null;
    return membership?.agentCooperationMode ?? membership?.cooperationMode ?? tokenMode;
  }

  getB2bAccessScope(): B2bAccessScope {
    const decoded = this.getDecodedAccessToken();
    const tokenAccessType = typeof decoded?.accessType === 'string' ? decoded.accessType : '';
    const tokenPartnerId = typeof decoded?.partnerId === 'string' ? decoded.partnerId : '';
    const tokenNetworkId = typeof decoded?.partnerNetworkId === 'string' ? decoded.partnerNetworkId : '';
    const tokenStoreId = typeof decoded?.partnerStoreId === 'string' ? decoded.partnerStoreId : '';
    const membership = this.currentMembership(tokenPartnerId, tokenStoreId);
    const roles = Array.isArray(decoded?.roles)
      ? decoded.roles
      : Array.isArray(decoded?.authorities)
      ? decoded.authorities
      : [];

    return {
      // Ограничен своим партнером/сетью/магазином только сотрудник без полной роли:
      // у админа в токене могут быть и роли оператора/менеджера, но partnerId нет
      restricted:
        roles.some(role => (B2B_SCOPED_ROLES as readonly string[]).includes(role)) &&
        !roles.some(role => (FULL_ACCESS_ROLES as readonly string[]).includes(role)),
      roles,
      // Партнер, сеть и точка — из /b2b/me/context; токен — запасной вариант, пока контекст не загружен
      accessType: membership?.accessType || tokenAccessType,
      partnerId: membership?.partnerId || tokenPartnerId,
      partnerNetworkId: membership?.partnerNetworkId || tokenNetworkId,
      partnerStoreId: membership?.partnerStoreId || tokenStoreId,
      membership,
    };
  }

  /**
   * Загружает GET /b2b/me/context для всех, кроме полного админа (у него нет привязки к партнеру).
   * Повторные вызовы для того же пользователя отдают кэш; ошибка — null (остаются данные из токена).
   */
  loadB2bContext(): Observable<B2bMeContext | null> {
    if (!this.getAccessToken() || this.hasAnyRole([...FULL_ACCESS_ROLES])) return of(null);

    const owner = this.currentUserKey();
    if (owner !== this.b2bContextOwner) this.resetB2bContext();
    if (this.b2bContext()) return of(this.b2bContext());
    if (this.b2bContextRequest$) return this.b2bContextRequest$;

    // Владелец фиксируется сразу — параллельный вызов во время запроса получит тот же запрос
    this.b2bContextOwner = owner;
    this.b2bContextRequest$ = this.http.get<B2bMeContext>(`${this.API_BASE}/b2b/me/context`).pipe(
      tap(context => this.b2bContext.set(context ?? null)),
      catchError(error => {
        console.error('Не удалось загрузить контекст пользователя (b2b/me/context):', error);
        return of(null);
      }),
      finalize(() => (this.b2bContextRequest$ = null)),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.b2bContextRequest$;
  }

  /**
   * Привязка текущего пользователя. Если их несколько — та, что совпадает с точкой/партнером из токена,
   * иначе первая.
   */
  private currentMembership(tokenPartnerId: string, tokenStoreId: string): B2bMembership | null {
    const memberships = this.b2bContext()?.memberships ?? [];
    return (
      memberships.find(m => !!tokenStoreId && m.partnerStoreId === tokenStoreId) ??
      memberships.find(m => !!tokenPartnerId && m.partnerId === tokenPartnerId) ??
      memberships[0] ??
      null
    );
  }

  private currentUserKey(): string | null {
    const decoded = this.getDecodedAccessToken();
    return decoded?.sub || decoded?.userId || decoded?.email || null;
  }

  private resetB2bContext(): void {
    this.b2bContext.set(null);
    this.b2bContextOwner = null;
    this.b2bContextRequest$ = null;
  }

  hasPermission(permission: string): boolean {
    const userInfo = this.getCurrentUserInfo();
    return userInfo?.permissions?.includes(permission) || false;
  }

  getTimeToExpiry(): number | null {
    const decoded = this.getDecodedAccessToken();
    if (!decoded?.exp) return null;

    const now = Math.floor(Date.now() / 1000);
    const timeLeft = decoded.exp - now;
    return timeLeft > 0 ? timeLeft * 1000 : 0;
  }

  logout(): void {
    this.clearAuthStorage();

    this.translate.get('Выход из системы успешно').subscribe(translation => {
      this.snackbarService.open(translation, 'snackbar-success');
    });

    this.router.navigate(['/login']);
  }

  clearAuthStorage(): void {
    this.resetB2bContext();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userCreds');
    localStorage.removeItem('userCredsForToken');
    localStorage.removeItem('language');
    localStorage.removeItem('country');
    localStorage.removeItem('policy');
  }

  refreshAccessToken(): Observable<Tokens> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token'));
    }

    return this.http.post<Tokens>(`${this.API_BASE}/users/refresh`, { refreshToken }).pipe(
      tap(tokens => {
        if (tokens.accessToken) {
          localStorage.setItem('accessToken', tokens.accessToken);
        }
        if (tokens.refreshToken) {
          localStorage.setItem('refreshToken', tokens.refreshToken);
        }
        this.ensureB2bAccess();
      })
    );
  }

  login(credentials: LoginModel): Observable<Tokens> {
    return this.http.post<Tokens>(`${this.API_BASE}/users/login`, credentials).pipe(
      tap(tokens => {
        localStorage.setItem('accessToken', tokens.accessToken);
        localStorage.setItem('refreshToken', tokens.refreshToken);

        const userInfo = this.getCurrentUserInfo();
        console.log('User logged in:', userInfo);
        this.ensureB2bAccess();

        this.translate.get('Вход в систему успешно').subscribe(translation => {
          this.snackbarService.open(translation, 'snackbar-success');
        });
      }),
      catchError(error => {
        console.error('Login failed', error);

        if (this.getBackendErrorMessage(error) === 'USER_INACTIVE') {
          this.snackbarService.error('Аккаунт неактивирован');
        } else if (error.message === 'Insufficient permissions') {
          this.translate.get('Недостаточно прав').subscribe(translation => {
            this.snackbarService.error(translation);
          });
        } else {
          this.translate.get('Ошибка при входе в систему').subscribe(translation => {
            this.snackbarService.error(translation);
          });
        }

        this.clearAuthStorage();
        return throwError(() => error);
      })
    );
  }

  private ensureB2bAccess(): void {
    if (this.hasAnyRole([...ADMIN_ALLOWED_ROLES])) return;

    this.clearAuthStorage();
    throw new Error('Insufficient permissions');
  }

  private getBackendErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object' || !('error' in error)) return null;

    const apiError = (error as { error?: unknown }).error;
    if (!apiError || typeof apiError !== 'object' || !('message' in apiError)) return null;

    const message = (apiError as { message?: unknown }).message;
    return typeof message === 'string' ? message : null;
  }
}
